import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ArrowLeft, Plus, Trash2, Play, Settings, Zap, HelpCircle, Shuffle } from 'lucide-react';
// Removed framer-motion import - not supported in this environment
import { useApp } from './AppContext';
import { YandexRTB } from './YandexRTB';
import type { Screen } from '../App';
import type { Marathon, Question } from './AppContext';

interface MarathonScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const MarathonScreen: React.FC<MarathonScreenProps> = ({ onNavigate, onBack }) => {
  const {
    marathons,
    addMarathon,
    deleteMarathon,
    updateMarathon,
    categories,
    questions,
    favorites,
    userStats,
    startTest,
    getBackgroundClass,
    endTest
  } = useApp();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [includeFavorites, setIncludeFavorites] = useState(false);
  const [includeIncorrect, setIncludeIncorrect] = useState(false);
  const [removingItems, setRemovingItems] = useState<Set<string>>(new Set());
  const [sourcesAnimating, setSourcesAnimating] = useState(false);

  // Функция для предварительного подсчета количества вопросов
  const getPreviewQuestionCount = () => {
    // Создаем Set для автоматического удаления дубликатов по ID
    const uniqueQuestionIds = new Set<string>();

    // Add questions from selected categories
    selectedCategories.forEach(category => {
      const categoryQuestions = questions.filter(q => q.category === category);
      categoryQuestions.forEach(q => uniqueQuestionIds.add(q.id));
    });

    // Add favorite questions
    if (includeFavorites) {
      const favoriteQuestions = questions.filter(q => favorites.has(q.id));
      favoriteQuestions.forEach(q => uniqueQuestionIds.add(q.id));
    }

    // Add incorrect questions
    if (includeIncorrect) {
      const incorrectQuestionIds = Object.entries(userStats.questionErrors)
        .filter(([_, errors]) => errors > 0)
        .map(([questionId]) => questionId);
      const incorrectQuestions = questions.filter(q => incorrectQuestionIds.includes(q.id));
      incorrectQuestions.forEach(q => uniqueQuestionIds.add(q.id));
    }

    return uniqueQuestionIds.size;
  };

  const generateMarathonName = () => {
    const parts: string[] = [];

    if (selectedCategories.length > 0) {
      if (selectedCategories.length <= 2) {
        parts.push(selectedCategories.join(' + '));
      } else {
        const [first, second] = selectedCategories;
        parts.push(`${first}, ${second} + ещё ${selectedCategories.length - 2}`);
      }
    }

    if (includeFavorites) {
      parts.push('Избранное');
    }

    if (includeIncorrect) {
      parts.push('Проблемные');
    }

    if (parts.length === 0) {
      return 'Марафон';
    }

    return parts.join(' + ');
  };

  const startMarathon = (marathon: Marathon) => {
    console.log('Starting marathon:', marathon);
    let marathonQuestions: any[] = [];

    // Add questions from selected categories
    marathon.categories.forEach(category => {
      const categoryQuestions = questions.filter(q => q.category === category);
      marathonQuestions.push(...categoryQuestions);
    });

    // Add favorite questions
    if (marathon.includeFavorites) {
      const favoriteQuestions = questions.filter(q => favorites.has(q.id));
      marathonQuestions.push(...favoriteQuestions);
    }

    // Add incorrect questions
    if (marathon.includeIncorrect) {
      const incorrectQuestionIds = Object.entries(userStats.questionErrors)
        .filter(([_, errors]) => errors > 0)
        .map(([questionId]) => questionId);
      const incorrectQuestions = questions.filter(q => incorrectQuestionIds.includes(q.id));
      marathonQuestions.push(...incorrectQuestions);
    }

    // Remove duplicates, сохраняя порядок первого вхождения
    const seenIds = new Set<string>();
    const uniqueQuestions = marathonQuestions.filter(q => {
      if (seenIds.has(q.id)) {
        return false;
      }
      seenIds.add(q.id);
      return true;
    });

    // Создаем Map для быстрого поиска индекса вопроса в исходном массиве questions
    const questionIndexMap = new Map<string, number>();
    questions.forEach((q, index) => {
      questionIndexMap.set(q.id, index);
    });

    // Сортируем вопросы по их исходному порядку в массиве questions
    const sortedUniqueQuestions = [...uniqueQuestions].sort((a, b) => {
      const indexA = questionIndexMap.get(a.id) ?? Infinity;
      const indexB = questionIndexMap.get(b.id) ?? Infinity;
      return indexA - indexB;
    });

    // Обновляем totalQuestions если изменилось количество доступных вопросов
    const currentTotalQuestions = sortedUniqueQuestions.length;
    if (currentTotalQuestions !== marathon.totalQuestions) {
      // Количество вопросов изменилось

      updateMarathon(marathon.id, {
        totalQuestions: currentTotalQuestions,
        // Если новое количество меньше завершенных - корректируем
        completedQuestions: Math.min(marathon.completedQuestions, currentTotalQuestions)
      });
    }

    // For resuming marathon, use the same order as before (don't shuffle)
    // For new marathon or completed marathon, check randomOrder setting
    let finalQuestions;
    let startIndex = 0;

    if (marathon.completedQuestions === 0 || marathon.completedQuestions >= currentTotalQuestions) {
      // New marathon or completed marathon
      // Если включен случайный порядок - перемешиваем, иначе используем исходный порядок
      if (marathon.randomOrder) {
        // Перемешиваем вопросы, но без повторений
        finalQuestions = [...sortedUniqueQuestions].sort(() => Math.random() - 0.5);
      } else {
        // Используем исходный порядок из массива questions (отсортированный по индексу)
        finalQuestions = [...sortedUniqueQuestions];
      }
      startIndex = 0;

      // Сохраняем порядок вопросов для последующего восстановления
      const questionOrder = finalQuestions.map(q => q.id);

      // Reset marathon progress if it was completed
      if (marathon.completedQuestions >= currentTotalQuestions) {
        updateMarathon(marathon.id, {
          completedQuestions: 0,
          sessionCorrectAnswers: 0, // Сбрасываем счетчик правильных ответов
          totalQuestions: currentTotalQuestions, // Обновляем актуальное количество
          questionOrder: questionOrder // Сохраняем новый порядок
        });
      } else {
        // Новый марафон - также сбрасываем sessionCorrectAnswers и сохраняем порядок
        updateMarathon(marathon.id, {
          sessionCorrectAnswers: 0,
          totalQuestions: currentTotalQuestions,
          questionOrder: questionOrder // Сохраняем порядок для нового марафона
        });
      }
    } else {
      // Resuming marathon - restore the same order from saved questionOrder
      if (marathon.questionOrder && marathon.questionOrder.length > 0) {
        // Восстанавливаем порядок из сохраненного массива ID
        finalQuestions = marathon.questionOrder
          .map(questionId => sortedUniqueQuestions.find(q => q.id === questionId))
          .filter(q => q !== undefined) as Question[];

        // Если некоторые вопросы не найдены (могли быть изменены), добавляем новые в конец в исходном порядке
        const existingIds = new Set(marathon.questionOrder);
        const newQuestions = sortedUniqueQuestions.filter(q => !existingIds.has(q.id));
        finalQuestions.push(...newQuestions);
      } else {
        // Fallback - если нет сохраненного порядка, используем отсортированный порядок
        finalQuestions = [...sortedUniqueQuestions];
      }

      startIndex = marathon.completedQuestions; // Правильный индекс для продолжения

      // При продолжении марафона НЕ сбрасываем sessionCorrectAnswers
      // Оставляем текущее значение для правильного отображения в TestScreen
    }

    // Запуск марафона
    console.log('Starting test with:', {
      finalQuestionsCount: finalQuestions.length,
      startIndex,
      marathonId: marathon.id
    });
    startTest('marathon', finalQuestions, undefined, marathon.id, startIndex);
    onNavigate('test');
  };

  const createMarathon = () => {
    // Точно так же собираем вопросы, как в startMarathon
    let marathonQuestions: any[] = [];

    // Add questions from selected categories
    selectedCategories.forEach(category => {
      const categoryQuestions = questions.filter(q => q.category === category);
      marathonQuestions.push(...categoryQuestions);
    });

    // Add favorite questions
    if (includeFavorites) {
      const favoriteQuestions = questions.filter(q => favorites.has(q.id));
      marathonQuestions.push(...favoriteQuestions);
    }

    // Add incorrect questions
    if (includeIncorrect) {
      const incorrectQuestionIds = Object.entries(userStats.questionErrors)
        .filter(([_, errors]) => errors > 0)
        .map(([questionId]) => questionId);
      const incorrectQuestions = questions.filter(q => incorrectQuestionIds.includes(q.id));
      marathonQuestions.push(...incorrectQuestions);
    }

    // Remove duplicates and get EXACT count
    const uniqueQuestions = marathonQuestions.filter((q, index, arr) =>
      arr.findIndex(item => item.id === q.id) === index
    );

    const exactTotalQuestions = uniqueQuestions.length;

    // Логирование для отладки
    const generatedName = generateMarathonName();

    console.log('Creating marathon:', {
      name: generatedName,
      categories: selectedCategories,
      includeFavorites,
      includeIncorrect,
      totalQuestions: exactTotalQuestions,
      uniqueQuestionsLength: uniqueQuestions.length
    });

    if (exactTotalQuestions === 0) {
      return;
    }

    const newMarathon: Marathon = {
      id: Date.now().toString(),
      name: generatedName,
      categories: selectedCategories,
      includeFavorites,
      includeIncorrect,
      completedQuestions: 0,
      totalQuestions: exactTotalQuestions, // Используем точное количество!
      sessionCorrectAnswers: 0, // Инициализируем счетчик правильных ответов
      questionOrder: undefined // Порядок будет создан при первом запуске
    };

    addMarathon(newMarathon);

    console.log('Marathon created:', newMarathon);

    // Reset form
    setSelectedCategories([]);
    setIncludeFavorites(false);
    setIncludeIncorrect(false);
    setShowCreateDialog(false);
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleDeleteMarathon = (marathonId: string) => {
    setRemovingItems(prev => new Set(prev).add(marathonId));

    // Задержка для анимации перед фактическим удалением
    setTimeout(() => {
      deleteMarathon(marathonId);
      setRemovingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(marathonId);
        return newSet;
      });
    }, 300); // Длительность анимации
  };

  const getMarathonProgress = (marathon: Marathon) => {
    if (marathon.totalQuestions <= 0) return 0;

    // Ensure completed questions never exceed total questions
    const validCompleted = Math.min(marathon.completedQuestions, marathon.totalQuestions);

    return (validCompleted / marathon.totalQuestions) * 100;
  };

  const previewQuestionCount = getPreviewQuestionCount();
  const canCreateMarathon = previewQuestionCount > 0;

  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      {/* Header */}
      <div className="app-header-fixed flex items-center">
        <button
          onClick={onBack}
          className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>

        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <Zap className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold glass-text truncate">Марафон</h1>
        </div>

        <button
          onClick={() => onNavigate('marathon-help')}
          className="ml-2 sm:ml-3 arrow-button flex-shrink-0"
          aria-label="Помощь"
        >
          <HelpCircle className="arrow-icon" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="app-content-scrollable">

        {/* Create Button - appears only when there are marathons */}
        {marathons.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <button
              onClick={() => setShowCreateDialog(true)}
              className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-3 glass-interactive rounded-lg glass-text"
              style={{ transition: 'none' }}
            >
              <Plus className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 glass-icon" />
              <span className="text-xs sm:text-sm md:text-base">Создать</span>
            </button>
          </div>
        )}

        {/* Marathon List */}
        {marathons.length === 0 ? (
          <div className="text-center py-6 sm:py-8 md:py-12 glass-panel rounded-xl">
            <div className="p-4 sm:p-6 md:p-8">
              <Settings className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 glass-text-muted mx-auto mb-3 sm:mb-4 md:mb-6 glass-icon" />
              <h3 className="font-medium mb-2 sm:mb-3 text-sm sm:text-base md:text-lg glass-text">Нет созданных марафонов</h3>
              <p className="text-xs sm:text-sm md:text-base glass-text-secondary mb-3 sm:mb-4 md:mb-6">
                Создайте свой первый марафон для персонализированного тестирования
              </p>
              <button
                onClick={() => setShowCreateDialog(true)}
                className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 md:px-5 md:py-3 mx-auto glass-interactive rounded-lg glass-text"
                style={{ transition: 'none' }}
              >
                <Plus className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 glass-icon" />
                <span className="text-xs sm:text-sm md:text-base">Создать марафон</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 md:space-y-6">
            {marathons.map((marathon) => {
              const progress = getMarathonProgress(marathon);
              const isRemoving = removingItems.has(marathon.id);

              return (
                <div
                  key={marathon.id}
                  className={`glass-panel rounded-xl transition-all duration-300 ${marathon.totalQuestions === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    } ${isRemoving ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                  onClick={(e) => {
                    // Проверяем, что клик не был по кнопке удаления, случайного порядка или их потомкам
                    if (e.target instanceof Element && (
                      e.target.closest('[data-delete-button]') ||
                      e.target.closest('[data-action-button]')
                    )) {
                      return;
                    }
                    if (marathon.totalQuestions > 0 && !isRemoving) {
                      startMarathon(marathon);
                    }
                  }}
                >
                  <div className="p-3 sm:p-4 md:p-6">
                    <div className="mb-3 sm:mb-4 md:mb-6">
                      <div className="flex items-center justify-between gap-1 sm:gap-2 mb-2 sm:mb-3">
                        <h3 className="font-medium text-sm sm:text-base md:text-lg glass-text flex-1 min-w-0">{marathon.name}</h3>
                        <div className="flex gap-2 flex-shrink-0 mt-2 mb-2">
                          <button
                            data-action-button="true"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              updateMarathon(marathon.id, {
                                randomOrder: !marathon.randomOrder
                              });
                            }}
                            disabled={isRemoving}
                            className={`p-2 sm:p-3 landscape-compact-p-1 landscape:p-2 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed glass-interactive glass-text ${marathon.randomOrder ? 'bg-white/25 dark:bg-black/50' : ''
                              }`}
                            style={{
                              position: 'relative',
                              zIndex: 10,
                              transition: 'none'
                            }}
                            title={marathon.randomOrder ? 'Случайный порядок включен' : 'Случайный порядок выключен'}
                          >
                            <Shuffle className={`h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 landscape:h-4 landscape:w-4 glass-icon ${marathon.randomOrder ? 'opacity-100' : 'opacity-60'
                              }`} />
                          </button>
                          <button
                            data-delete-button="true"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteMarathon(marathon.id);
                            }}
                            disabled={isRemoving}
                            className="p-2 sm:p-3 landscape-compact-p-1 landscape:p-2 glass-interactive rounded-lg glass-text flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              position: 'relative',
                              zIndex: 10,
                              transition: 'none'
                            }}
                          >
                            <div className={`transition-all duration-300 ${isRemoving ? 'animate-pulse scale-75' : ''}`}>
                              <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 landscape:h-4 landscape:w-4 glass-icon" />
                            </div>
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex justify-between text-xs sm:text-sm md:text-base glass-text-secondary">
                          <span>
                            {marathon.completedQuestions >= marathon.totalQuestions
                              ? 'Марафон завершен! Нажмите для повтора'
                              : marathon.completedQuestions > 0
                                ? 'Нажмите для продолжения'
                                : 'Нажмите для запуска'
                            } •
                            {marathon.completedQuestions} из {marathon.totalQuestions} вопросов
                          </span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <div className="w-full glass-progress-bg rounded-full h-2 sm:h-3">
                          <div
                            className="glass-progress-fill h-2 sm:h-3 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Нативный рекламный блок (показывается всегда внизу списка или сообщения об отсутствии марафонов) */}
        <div className="w-full mt-4 sm:mt-6 mb-4 sm:mb-6 flex justify-center rounded-xl bg-white/5 min-h-[100px]">
          <YandexRTB type="native" idMobile="R-A-17579918-7" idDesktop="R-A-17579918-7" />
        </div>

        {/* Create Marathon Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) {
            setSourcesAnimating(false);
          } else {
            // Запускаем анимацию источников при открытии диалога
            setTimeout(() => setSourcesAnimating(true), 200);
          }
        }}>
          <DialogContent
            className="max-w-[90vw] sm:max-w-xl md:max-w-2xl lg:max-w-3xl w-[90vw] sm:w-auto max-h-[85vh] sm:max-h-[80vh] overflow-y-auto p-4 sm:p-6"
            style={{
              '--dialog-top': `calc(env(safe-area-inset-top, 0px) + 88px)`,
              '--dialog-translate-y': '0%',
            } as React.CSSProperties}
            aria-describedby={undefined}
          >
            <DialogHeader className={sourcesAnimating ? 'marathon-header-animate' : ''}>
              <DialogTitle>Создать марафон</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col h-full">
              {/* Sources section */}
              <div className="flex-1 mt-2 min-h-0">
                <Label className={`mb-3 block ${sourcesAnimating ? 'marathon-input-animate' : ''}`}
                  style={{ animationDelay: sourcesAnimating ? '0.18s' : '0s' }}>
                  Выберите источники вопросов
                </Label>
                <div className={`marathon-sources-list-expanded ${sourcesAnimating ? 'marathon-sources-animate-in' : ''}`}>
                  {categories.map((category, index) => (
                    <div
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className={`marathon-source-item cursor-pointer select-none ${sourcesAnimating ? 'marathon-source-item-animate' : ''
                        } ${selectedCategories.includes(category)
                          ? 'marathon-source-selected'
                          : 'marathon-source-unselected'
                        }`}
                    >
                      <div className="flex items-center justify-between h-full">
                        <span className="text-sm font-medium flex-1 min-w-0">
                          {category}
                        </span>
                        <span className="text-sm opacity-70 flex-shrink-0 my-[0px] mx-[9px]">
                          {questions.filter(q => q.category === category).length}
                        </span>
                      </div>
                    </div>
                  ))}

                  <div
                    onClick={() => setIncludeFavorites(!includeFavorites)}
                    className={`marathon-source-item cursor-pointer select-none ${sourcesAnimating ? 'marathon-source-item-animate' : ''
                      } ${includeFavorites
                        ? 'marathon-source-selected'
                        : 'marathon-source-unselected'
                      }`}
                    style={{ animationDelay: sourcesAnimating ? `${categories.length * 0.05 + 0.1}s` : '0s' }}
                  >
                    <div className="flex items-center justify-between h-full">
                      <span className="text-sm font-medium flex-1 min-w-0">
                        Включить избранные вопросы
                      </span>
                      <span className="text-sm opacity-70 flex-shrink-0 ml-2">
                        {favorites.size}
                      </span>
                    </div>
                  </div>

                  <div
                    onClick={() => setIncludeIncorrect(!includeIncorrect)}
                    className={`marathon-source-item cursor-pointer select-none ${sourcesAnimating ? 'marathon-source-item-animate' : ''
                      } ${includeIncorrect
                        ? 'marathon-source-selected'
                        : 'marathon-source-unselected'
                      }`}
                    style={{ animationDelay: sourcesAnimating ? `${categories.length * 0.05 + 0.15}s` : '0s' }}
                  >
                    <div className="flex items-center justify-between h-full">
                      <span className="text-sm font-medium flex-1 min-w-0">
                        Включить проблемные вопросы
                      </span>
                      <span className="text-sm opacity-70 flex-shrink-0 ml-2">
                        {Object.entries(userStats.questionErrors).filter(([_, errors]) => errors > 0).length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total questions count - positioned close to button */}
              <div className={`flex-shrink-0 mt-2 mb-1 ${sourcesAnimating ? 'marathon-counter-animate' : ''}`}>
                <div className="flex items-center justify-between px-1">
                  <span className="text-sm font-medium glass-text">
                    Всего вопросов в марафоне:
                  </span>
                  <span className="text-base font-semibold glass-text">
                    {previewQuestionCount}
                  </span>
                </div>
              </div>
            </div>

            <div className={`flex pt-1 ${sourcesAnimating ? 'marathon-button-animate' : ''}`}>
              <div
                onClick={createMarathon}
                className={`flex-1 flex items-center justify-center px-4 py-3 rounded-md text-sm font-medium cursor-pointer select-none transition-all duration-200 ${canCreateMarathon
                    ? 'bg-white/10 backdrop-blur-md border border-white/20 glass-text active:bg-white/25 focus-visible:bg-white/20 dark:bg-black/30 dark:border-white/15 dark:active:bg-black/50 dark:focus-visible:bg-black/40'
                    : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                  }`}
                style={{
                  pointerEvents: canCreateMarathon ? 'auto' : 'none'
                }}
              >
                Создать марафон
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};